import React, { useState, useEffect } from "react";
import { Layout, Row, Col, Spin, Empty, message } from "antd";
import { useCustom } from "@refinedev/core";
import { ConversationList, Thread } from "./ConversationList";
import { ConversationView } from "./ConversationView";
import { MessageComposer } from "./MessageComposer";
import "./GuestCommunication.css";

const { Content, Sider } = Layout;

interface Message {
  id: number;
  guest_uid: number;
  owner_uid: number;
  subject: string;
  body: string;
  body_preview: string;
  direction: "incoming" | "outgoing";
  email_address_from: string;
  email_address_to: string;
  message_id: string;
  in_reply_to: string;
  received_at: number;
  is_read: boolean;
  attachments: any[];
}

export const GuestCommunication: React.FC = () => {
  const [selectedGuestUid, setSelectedGuestUid] = useState<number | undefined>();
  const [selectedThread, setSelectedThread] = useState<Thread | undefined>();

  // Fetch all threads
  const { data: threadsData, isLoading: threadsLoading, refetch: refetchThreads } = useCustom<Thread[]>({
    url: "/api/mailbox/threads",
    method: "get",
  });

  // Fetch messages for selected guest
  const { data: messagesData, isLoading: messagesLoading, refetch: refetchMessages } = useCustom<Message[]>({
    url: "/api/mailbox/thread",
    method: "get",
    config: {
      query: {
        guest: selectedGuestUid,
      },
    },
    queryOptions: {
      enabled: !!selectedGuestUid,
    },
  });

  const threads: Thread[] = (threadsData?.data as Thread[]) || [];
  const messages: Message[] = (messagesData?.data as Message[]) || [];

  useEffect(() => {
    console.log('Threads API Response:', threadsData);
    console.log('Threads:', threads);
  }, [threadsData, threads]);

  useEffect(() => {
    console.log('Messages API Response:', messagesData);
    console.log('Messages:', messages);
  }, [messagesData, messages]);

  const handleThreadSelect = (guestUid: number) => {
    setSelectedGuestUid(guestUid);
    const thread = threads.find(t => t.guest_uid === guestUid);
    setSelectedThread(thread);
    
    // Mark messages as read when selecting a thread
    if (thread && thread.unread_count > 0) {
      markThreadAsRead(guestUid);
    }
  };

  const markThreadAsRead = async (guestUid: number) => {
    try {
      const response = await fetch(`/api/mailbox/mark-thread-read?guest=${guestUid}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        // Refresh threads to update unread count
        refetchThreads();
      }
    } catch (error) {
      console.error('Error marking thread as read:', error);
    }
  };

  const handleSendMessage = async (data: { subject?: string; body: string; attachments?: any[] }) => {
    if (!selectedGuestUid || !selectedThread) {
      message.error("Kein Gast ausgewählt");
      return;
    }

    try {
      const response = await fetch('/api/mailbox/send', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          guest_uid: selectedGuestUid,
          subject: data.subject || 'Kein Betreff',
          body: data.body,
          attachments: data.attachments || [],
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        message.success("Nachricht gesendet");
        // Refresh messages and threads
        refetchMessages();
        refetchThreads();
      } else {
        throw new Error(result.error || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      message.error("Fehler beim Senden der Nachricht");
    }
  };

  return (
    <div className="guest-communication">
      <Row style={{ height: "100%" }}>
        <Col xs={24} sm={24} md={8} lg={6} className="conversation-list-col">
          <ConversationList
            threads={threads}
            selectedGuestUid={selectedGuestUid}
            onThreadSelect={handleThreadSelect}
          />
        </Col>
        <Col xs={24} sm={24} md={16} lg={18} className="conversation-view-col">
          <Layout className="chat-layout">
            <Content className="chat-content">
              {selectedGuestUid ? (
                <ConversationView
                  messages={messages}
                  loading={messagesLoading}
                  guestInfo={selectedThread}
                />
              ) : (
                <div className="no-conversation">
                  <Empty description="Wählen Sie eine Konversation aus" />
                </div>
              )}
            </Content>
            {selectedGuestUid && (
              <div className="chat-footer">
                <MessageComposer onSend={handleSendMessage} />
              </div>
            )}
          </Layout>
        </Col>
      </Row>
    </div>
  );
}; 